import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import TypingIndicator from "./TypingIndicator";
import styles from "./SupportChat.module.css";
import io from "socket.io-client";
import {
  WEB_PUBSUB_SOCKETIO_ENDPOINT,
  WEB_PUBSUB_CLIENT_PATH_SUPPORT_CHAT,
  URLS,
} from "../../constants";
import { Button, Typography } from "@mui/material";
import { uvenuFetcher } from "../../utils/uvenu-fetcher";

const socket = io(WEB_PUBSUB_SOCKETIO_ENDPOINT, {
  path: WEB_PUBSUB_CLIENT_PATH_SUPPORT_CHAT,
  transports: ["websocket"],
  autoConnect: false,
});
const ChatPanel = ({ isOpen, onClose, user }) => {
  const [messages, setMessages] = useState([
    {
      id: "1",
      content: "Hello! How can I help you today?",
      type: "text",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [status, setStatus] = useState("");
  //const [replies, setReplies] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const onMessage = (reply) => {
    setStatus("Support replied!");

    setIsTyping(true);

    const newMessage = {
      id: Date.now().toString(),
      text: reply.content, // Use the reply content from the server
      isUser: false,
      timestamp: new Date(),
    };
    if (reply.type === "image") {
      delete newMessage.text;
    }
    setMessages((prev) => [...prev, { ...newMessage, ...reply }]);
    setIsTyping(false);
  };

  const handleSendMessage = (messageText) => {
    const newMessage = {
      id: Date.now().toString(),
      type: "text",
      content: messageText,
      isUser: true,
      timestamp: new Date(),
      sentBy: user?.email || "Admin User", // Use user email if available
    };

    setMessages((prev) => [...prev, newMessage]);
    if (messageText.trim()) {
      socket.emit(
        "sendMessage",
        JSON.stringify({
          messageText,
          sentBy: user?.email || "Admin User",
          isUser: true,
        })
      );
    }
  };

  const getHistory = async () => {
    try {
      const response = await uvenuFetcher({
        method: "GET",

        url: URLS.GET_SUPPORT_MESSAGES(user?.email),
        headers: {
          token: `${user.token}`,
        },
      });
      if (response.statusCode === 200) {
        if (response.json.messages) {
          setMessages((prev) => {
            const merged = [
              ...prev,
              ...response.json.messages.map((msg) => ({
                id: msg._id,
                content: msg.content,
                type: msg.type,
                isUser: msg.isUser,
                timestamp: new Date(msg.timestamp),
                caption: msg.caption || "",
                sentBy: msg.sentBy,
              })),
            ];
            const uniqueById = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            return uniqueById;
          });
        }
      } else {
        return [];
      }
    } catch {}
  };

  useEffect(() => {
    getHistory();
    socket.on("connect", () => {
      console.log("Connected to backend!");
      setStatus("connected");
    });

    socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
      setStatus("failed to connect");
    });

    // Listen for server responses
    socket.on("messageSent", (msg) => {
      setStatus(msg);
    });

    socket.on("messageError", (error) => {
      setStatus(error);
    });

    socket.on("supportReply", onMessage);
    // Cleanup on unmount
    return () => {
      socket.off("connect");
      socket.off("connect_error");
      socket.off("messageSent");
      socket.off("messageError");
      socket.off("supportReply");
    };
  }, []);
  useEffect(() => {
    if (isOpen) {
      socket.connect();
    } else {
      socket.disconnect();
    }
    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.chatPanel}>
      {/* Header */}
      <div className={styles.chatPanelHeader}>
        <div className={styles.chatPanelTitle}>
          <div
            className={
              status === "failed to connect"
                ? `${styles.chatPaneltitleInner} ${styles.red}`
                : `${styles.chatPaneltitleInner} ${styles.green}`
            }
          ></div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography className={styles.chatPanelTitleText}>
              Uvenu Support
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.6)" }}
            >
              {status}
            </Typography>
          </div>
        </div>
        <Button
          variant="outlined"
          onClick={onClose}
          className={styles.minimizeButton}
        >
          <X size="medium" className={styles.chatPanelActionIcons} />
        </Button>
      </div>

      {/* Messages */}
      <div className={styles.chatPanelMessages}>
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isUser={message.isUser}
            timestamp={message.timestamp}
          />
        ))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSendMessage={handleSendMessage} disabled={isTyping} />
    </div>
  );
};

export default ChatPanel;
